import {
  ApiError,
  AuthenticationService,
  OpenAPI,
  TCreateGroupRequest,
  TCreateUserRequest,
  TLoginRequest,
  TOAuthProvider,
  User,
  UserService,
} from '@talon/web/lib/api';
import Storage from '@talon/web/src/utils/storage';
import * as jwt from 'jsonwebtoken';
import { action, computed, makeObservable, observable, runInAction, toJS } from 'mobx';
import { config } from '../../config';
import { BaseStore } from './baseStore'; // for some dumb reason, these have to be imported individually
import { RootStore } from './rootStore'; // for some dumb reason, these have to be imported individually

export class UserStore extends BaseStore {
  _user: User;
  _jwt: string;
  _restoring: boolean;
  // Explicit signal for "the initial token/user restore from storage hasn't resolved yet" —
  // distinct from "there's genuinely no session." Starts true only if a token is actually in
  // storage (there's something to wait for); consumers like RequireAuth gate on this instead of
  // inferring loading-vs-logged-out from `token`/`user`'s transient empty-placeholder values,
  // which caused a race (see specs/unified-chat.md BUG-4): `_user` starts as `{}` (truthy) and
  // `_jwt` starts as `''` (falsy) regardless of whether a real session exists, so on a fresh page
  // load there was no reliable way to tell "still loading" apart from "actually logged out."
  _bootstrapping: boolean;

  constructor(root: RootStore) {
    super(root);
    this._user = {} as User;
    this._jwt = '';
    this._restoring = false;
    this._bootstrapping = Boolean(Storage.getToken());

    if (this.token) {
      OpenAPI.TOKEN = this.token;
    }
    makeObservable(this, {
      _user: observable,
      _jwt: observable,
      _bootstrapping: observable,
      token: computed,
      user: computed,
      isBootstrapping: computed,
      restoreFromStorage: action,
      logout: action,
      confirmEmail: action,
      applySession: action,
    });
  }

  restoreFromStorage = async () => {
    if (this._restoring) return;

    const token = Storage.getToken();
    if (!token) {
      // Guard the write: this branch is reached on every read of `token`/`user` while logged
      // out (their getters call `restoreFromStorage()` on every access as long as `_jwt`/`_user`
      // stay empty), which is every render of every observer component that reads them. Writing
      // to an observable unconditionally here — even to the same value — re-enters a MobX action
      // from inside a computed's evaluation on every single one of those renders, which was
      // causing "Maximum update depth exceeded" (RootContextProvider) once anything also observed
      // `isBootstrapping`. Only actually write when there's a real transition to make.
      if (this._bootstrapping) {
        runInAction(() => {
          this._bootstrapping = false;
        });
      }
      return;
    }

    this._restoring = true;
    console.log('**Restoring JWT from storage**');
    this._jwt = OpenAPI.TOKEN = token;
    const JwtUser = jwt.decode(token || '', { json: true })?.data as User;
    try {
      const { user } = await this.q(UserService.getUser(JwtUser.id, 'groups,active_group'));
      if (!user) {
        throw new Error('User not found');
      }
      runInAction(() => {
        this._user = user;
      });
    } catch (e) {
      this.handleError(e);
      console.log('**JWT is invalid or user not found, logging out**');
      this.logout();
    } finally {
      this._restoring = false;
      runInAction(() => {
        this._bootstrapping = false;
      });
    }
  };

  get token(): string {
    if (!this._jwt) {
      // the user has refreshed the page
      // get the jwt token from storage
      // and set the jwt token
      this.restoreFromStorage();
    }
    return this._jwt;
  }

  get user(): User {
    if (!this._user?.id) {
      // the user has refreshed the page
      this.restoreFromStorage();
    }
    return toJS(this._user);
  }

  /** True only while a stored token's restore is still in flight — see the field comment above. */
  get isBootstrapping(): boolean {
    return this._bootstrapping;
  }
  public signup = async ({
    user,
    group,
    token,
    talent_manager,
  }: {
    user: TCreateUserRequest;
    group?: TCreateGroupRequest;
    token?: string;
    talent_manager?: boolean;
  }) => {
    const req = {
      user,
      group,
      token,
      talent_manager,
    };
    try {
      return await this.q(UserService.signUp(req));
    } catch (e) {
      this.handleError(e, config.toast.signup.error(e as ApiError));
    }
  };

  public resendConfirmationEmail = async () => {
    try {
      await this.q(UserService.resendConfirmationEmail());
    } catch (e) {
      this.handleError(e);
    }
  };

  public confirmEmail = async (t: string) => {
    try {
      const { jwt, user } = await this.q(UserService.confirmEmail({ token: t }));
      runInAction(() => {
        this._user = user;
        this._jwt = OpenAPI.TOKEN = jwt;
        Storage.setToken(jwt);
      });
    } catch (e) {
      this.handleError(e);
    }
  };

  public login = async (req: TLoginRequest) => {
    try {
      const res = await this.q(AuthenticationService.login(req));
      runInAction(() => {
        this._user = res.user;
        this._jwt = OpenAPI.TOKEN = res.jwt;
        Storage.setToken(res.jwt);
      });
      return true;
    } catch (e) {
      this.handleError(e, config.toast.login.error(e as ApiError));
      return false;
    }
  };

  /**
   * Switches the active session to an already-minted `{ jwt, user }` pair without making a
   * login request — the same assignment `login()` does inline above, factored out so
   * impersonation (adminStore.impersonateUser / exitImpersonation) can reuse it instead of
   * duplicating it.
   */
  public applySession = (jwt: string, user: User) => {
    runInAction(() => {
      this._user = user;
      this._jwt = OpenAPI.TOKEN = jwt;
      Storage.setToken(jwt);
    });
  };

  public logout = () => {
    // If there's genuinely nothing to clear, don't touch `_user`/`_jwt` — reassigning `_user` to a
    // fresh `{}` every call (even when it's already empty) creates a new object reference each
    // time, which defeats MobX's memoization of the `user` computed. Consumers like RequireAuth
    // call `logout()` from an effect that has `user` in its dependency array specifically to
    // handle "no valid session," so an unguarded reassignment here caused that effect to see
    // `user` "change" on every run and re-fire itself forever — an infinite render loop for any
    // genuinely-unauthenticated visit to a protected route.
    if (!this._jwt && !this._user?.id) {
      Storage.removeLogInInfo();
      OpenAPI.TOKEN = '';
      return;
    }

    this._user = {} as User;
    this._jwt = '';
    this._bootstrapping = false;
    this.rootStore.talentManagerStore.reset();
    Storage.removeLogInInfo();
    OpenAPI.TOKEN = '';
  };

  public updateProfile = async (profile: User) => {
    try {
      const { user } = await this.q(UserService.updateUser(profile.id, profile));
      runInAction(() => {
        this._user = { ...this._user, ...user };
      });
    } catch (e) {
      this.handleError(e);
    }
  };

  public changeActiveGroup = async (groupId: string) => {
    try {
      const { group } = await this.q(UserService.changeActiveGroup(this.user.id, { group_id: groupId }));
      runInAction(() => {
        this._user = { ...this._user, active_group: group };
      });
    } catch (e) {
      this.handleError(e);
    }
  };

  public changePassword = async (oldPassword: string, newPassword: string) => {
    try {
      await this.q(UserService.changePassword(this.user.id, { old_password: oldPassword, new_password: newPassword }));
      return true;
    } catch (e) {
      this.handleError(e);
    }
  };

  // forgot password
  public forgotPassword = async (email: string) => {
    try {
      await this.q(UserService.forgotPassword({ email }));
      return true;
    } catch (e) {
      this.handleError(e);
    }
    return false;
  };

  public validateResetPasswordToken = async (token: string) => {
    try {
      const { user } = await this.q(UserService.validatePasswordResetToken(token));
      return user.id;
    } catch (e) {
      this.handleError(e);
    }
  };

  public resetPassword = async (user_id: string, token: string, password: string) => {
    try {
      return await this.q(UserService.resetPassword({ user_id, token, password }));
    } catch (e) {
      this.handleError(e);
    }
  };

  public isEmailAvailable = async (email: string): Promise<boolean> => {
    if (!email) {
      return true;
    }
    try {
      const res = await this.q(UserService.isEmailAvailable(email));
      return res;
    } catch (e) {
      this.handleError(e);
      return false;
    }
  };

  public refreshUser = async (): Promise<void> => {
    try {
      const { user } = await this.q(UserService.getUser(this._user.id, 'groups,active_group'));
      runInAction(() => {
        if (user) {
          this._user = user;
        }
      });
    } catch (e) {
      this.handleError(e);
    }
  };

  public getOAuthProfile = async (userId: string, provider: string): Promise<void> => {
    // Initialize oauth_profiles if undefined
    if (!this._user.oauth_profiles) {
      this._user.oauth_profiles = [];
    }

    if (!this._user.oauth_profiles.find((p) => p.provider === provider)) {
      try {
        const res = await this.q(UserService.getUserOAuthProfile(userId, provider as TOAuthProvider));
        runInAction(() => {
          console.log(`Fetched OAuth profile for ${provider}:`, res);
          this._user.oauth_profiles.push({
            ...res.profile,
          });
        });
      } catch (e) {
        // Show user-friendly message if token expired
        if ((e as ApiError).status === 401 || (e as ApiError).status === 404) {
          // Use handleError which will show the toast
          this.handleError(new Error('OAuth connection expired or not found. Please reconnect.'));
        } else {
          this.handleError(e);
        }
      }
    }
  };

  public disconnectOAuthProfile = async (userId: string, provider: TOAuthProvider): Promise<void> => {
    try {
      await this.q(UserService.disconnectOAuthProfile(userId, provider));
      runInAction(() => {
        this._user.oauth_profiles = this._user.oauth_profiles.filter((p) => p.provider !== provider);
      });
    } catch (e) {
      this.handleError(e);
    }
  };
}
